/**
 * Auto-Export Service - F068 Continuous Export
 * Handles automatic export on save with debouncing and throttling.
 *
 * Features:
 * - Debounced export (wait for user to stop editing)
 * - Throttled export (max 1 export per minute)
 * - Event-driven triggers (object created/updated)
 * - Enable/disable via settings
 *
 * Related: ADR-002 Hybrid Architecture Strategy
 */

import { eventBus } from '../events/EventBus';
import {
  exportToZipOrganized,
  uploadBackupToStorage,
  uploadBackupToDrive,
  uploadBackupToBoth,
  loadExportSettings,
  type ExportSettings,
  type ExportOptions
} from './exportEnhancedService';
import { uploadMarkdownFilesToDrive } from './driveFileExportService';

class AutoExportService {
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private lastExportTime: number = 0;
  private isEnabled: boolean = false;
  private settings: ExportSettings | null = null;
  private isExporting: boolean = false;
  private unsubscribers: Array<() => void> = []; // Store unsubscribe functions

  // Constants
  private readonly MIN_EXPORT_INTERVAL_MS = 60000; // 1 minute throttle
  private readonly DEFAULT_DEBOUNCE_MS = 3000; // 3 second debounce

  /**
   * Initialize auto-export service
   */
  async initialize(): Promise<void> {
    console.log('[AutoExport] Initializing...');

    // Load user settings
    await this.loadSettings();

    // Listen for data changes if enabled
    if (this.isEnabled) {
      this.startListening();
      console.log('[AutoExport] ✅ Auto-export enabled');
    } else {
      console.log('[AutoExport] ⏸️ Auto-export disabled');
    }
  }

  /**
   * Start listening for data changes
   */
  private startListening(): void {
    // Store unsubscribe functions so we can clean up later
    this.unsubscribers.push(
      eventBus.on('object:updated', this.handleDataChange.bind(this))
    );
    this.unsubscribers.push(
      eventBus.on('object:created', this.handleDataChange.bind(this))
    );
    this.unsubscribers.push(
      eventBus.on('block:updated', this.handleDataChange.bind(this))
    );
    this.unsubscribers.push(
      eventBus.on('block:created', this.handleDataChange.bind(this))
    );
    console.log('[AutoExport] 👂 Listening for data changes');
  }

  /**
   * Stop listening for data changes
   */
  private stopListening(): void {
    // Call all unsubscribe functions
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];
    console.log('[AutoExport] 🔇 Stopped listening');
  }

  /**
   * Load export settings from database
   */
  private async loadSettings(): Promise<void> {
    this.settings = await loadExportSettings();

    if (this.settings) {
      this.isEnabled = this.settings.auto_export_enabled;
      console.log('[AutoExport] Settings loaded:', {
        enabled: this.isEnabled,
        debounce: this.settings.debounce_ms,
        organization: this.settings.folder_organization
      });
    } else {
      this.isEnabled = false;
      console.log('[AutoExport] No settings found, auto-export disabled');
    }
  }

  /**
   * Handle data change event (debounced + throttled)
   */
  private handleDataChange(eventData?: any): void {
    if (!this.isEnabled) return;
    if (this.isExporting) {
      console.log('[AutoExport] ⏸️ Export already in progress, skipping');
      return;
    }

    const now = Date.now();
    const timeSinceLastExport = now - this.lastExportTime;

    // Throttle: Skip if exported too recently
    if (timeSinceLastExport < this.MIN_EXPORT_INTERVAL_MS) {
      const remainingMs = this.MIN_EXPORT_INTERVAL_MS - timeSinceLastExport;
      const remainingSec = Math.ceil(remainingMs / 1000);
      console.log(`[AutoExport] ⏳ Throttled (wait ${remainingSec}s)`);
      return;
    }

    // Clear existing debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set new debounce timer
    const debounceMs = this.settings?.debounce_ms || this.DEFAULT_DEBOUNCE_MS;
    console.log(`[AutoExport] ⏱️ Debouncing (${debounceMs}ms)...`);

    this.debounceTimer = setTimeout(() => {
      this.performExport();
    }, debounceMs);
  }

  /**
   * Perform actual export
   */
  private async performExport(): Promise<void> {
    if (this.isExporting) {
      console.log('[AutoExport] Export already in progress');
      return;
    }

    this.isExporting = true;
    this.lastExportTime = Date.now();

    console.log('[AutoExport] 🔄 Starting auto-export...');

    try {
      const options: ExportOptions = {
        organization: this.settings?.folder_organization || 'type',
        includeAttachments: this.settings?.export_attachments ?? true,
        includeFrontmatter: this.settings?.include_frontmatter ?? true,
        frontmatterFields: this.settings?.frontmatter_properties
      };

      // Determine destination from settings
      const destination = this.settings?.backup_destination || 'supabase';
      console.log(`[AutoExport] 📤 Uploading backup to ${destination}...`);

      let backupInfo = null;

      if (destination === 'supabase') {
        // Supabase: Still use ZIP
        const blob = await exportToZipOrganized(options, (progress) => {
          if (progress.status === 'exporting') {
            console.log(`[AutoExport] ${progress.message}`);
          }
        });
        backupInfo = await uploadBackupToStorage(blob, options, (progress) => {
          if (progress.status === 'complete') {
            console.log(`[AutoExport] ${progress.message}`);
          }
        });
      } else if (destination === 'drive') {
        // Drive: Use individual files (NEW!)
        console.log('[AutoExport] 📁 Using individual file export for Drive');
        backupInfo = await uploadMarkdownFilesToDrive(options, (progress) => {
          if (progress.status === 'uploading') {
            console.log(`[AutoExport] ${progress.message} (${progress.current}/${progress.total})`);
          } else if (progress.status === 'complete') {
            console.log(`[AutoExport] ${progress.message}`);
          }
        });
      } else if (destination === 'both') {
        // Both: ZIP to Supabase, individual files to Drive
        console.log('[AutoExport] 📦 ZIP to Supabase, 📁 Individual files to Drive');

        // Upload ZIP to Supabase
        const blob = await exportToZipOrganized(options, (progress) => {
          if (progress.status === 'exporting') {
            console.log(`[AutoExport] [Supabase] ${progress.message}`);
          }
        });
        const supabaseBackup = await uploadBackupToStorage(blob, options, (progress) => {
          if (progress.status === 'complete') {
            console.log(`[AutoExport] [Supabase] ${progress.message}`);
          }
        });

        // Upload individual files to Drive
        const driveBackup = await uploadMarkdownFilesToDrive(options, (progress) => {
          if (progress.status === 'uploading') {
            console.log(`[AutoExport] [Drive] ${progress.message} (${progress.current}/${progress.total})`);
          } else if (progress.status === 'complete') {
            console.log(`[AutoExport] [Drive] ${progress.message}`);
          }
        });

        // Use Supabase backup info as primary
        backupInfo = supabaseBackup || driveBackup;
      }

      if (backupInfo) {
        console.log(`[AutoExport] ✅ Backup saved to ${destination}`);
        // Emit success event
        eventBus.emit('export:complete', {
          type: 'auto',
          backupId: backupInfo.id,
          size: backupInfo.file_size,
          destination
        });
      } else {
        throw new Error(`Failed to upload backup to ${destination}`);
      }

    } catch (error: any) {
      console.error('[AutoExport] ❌ Auto-export failed:', error);

      // Emit error event
      eventBus.emit('export:error', { type: 'auto', error: error.message });
    } finally {
      this.isExporting = false;
    }
  }

  /**
   * Manually trigger export (bypass throttle)
   */
  async triggerManualExport(): Promise<void> {
    console.log('[AutoExport] 🖱️ Manual export triggered');
    this.lastExportTime = 0; // Reset throttle
    await this.performExport();
  }

  /**
   * Update settings and reload
   */
  async updateSettings(newSettings: Partial<ExportSettings>): Promise<void> {
    const wasEnabled = this.isEnabled;

    // Merge with existing settings
    this.settings = this.settings
      ? { ...this.settings, ...newSettings }
      : { ...newSettings } as ExportSettings;

    this.isEnabled = this.settings.auto_export_enabled;

    console.log('[AutoExport] Settings updated:', {
      enabled: this.isEnabled,
      debounce: this.settings.debounce_ms,
      organization: this.settings.folder_organization,
      destination: this.settings.backup_destination || 'supabase'
    });

    // Start/stop listening based on new state
    if (this.isEnabled && !wasEnabled) {
      this.startListening();
      console.log('[AutoExport] ✅ Auto-export enabled');
    } else if (!this.isEnabled && wasEnabled) {
      this.stopListening();
      console.log('[AutoExport] ⏸️ Auto-export disabled');
    }
  }

  /**
   * Get current status
   */
  getStatus(): {
    enabled: boolean;
    isExporting: boolean;
    lastExportTime: number;
    nextAllowedExportTime: number;
  } {
    const now = Date.now();
    const nextAllowed = this.lastExportTime + this.MIN_EXPORT_INTERVAL_MS;

    return {
      enabled: this.isEnabled,
      isExporting: this.isExporting,
      lastExportTime: this.lastExportTime,
      nextAllowedExportTime: Math.max(nextAllowed, now)
    };
  }

  /**
   * Cleanup and stop service
   */
  destroy(): void {
    console.log('[AutoExport] Shutting down...');

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.stopListening();
    this.isEnabled = false;

    console.log('[AutoExport] 🛑 Stopped');
  }
}

// Export singleton instance
export const autoExportService = new AutoExportService();
